import React from 'react';
import PropTypes from 'prop-types';
import { useOutletContext } from 'react-router-dom-v5-compat';

import {
  ALERT_TYPES,
  APPEAL_TYPES,
  STATUS_TYPES,
} from '~/platform/shared/claims-appeals';
import {
  getAlertContent,
  getStatusContents,
  getNextEvents,
  APPEAL_ACTIONS,
} from '../utils/appeals-v2-helpers';

import CurrentStatus from '../components/appeals-v2/CurrentStatus';
import AlertsList from '../components/appeals-v2/AlertsList';
import WhatsNext from '../components/appeals-v2/WhatsNext';
import Docket from '../components/appeals-v2/Docket';
import PastEventsSection from '../components/appeals-v2/PastEventsSection';

/**
 * AppealsV2StatusPage is in charge of the layout of the status page
 */
export default function AppealsV2StatusPage({
  appeal: propAppeal,
  fullName: propFullName,
}) {
  // Flag off: rendered as an Outlet child (appeal/fullName from context). Flag
  // on: a va-tabs panel with those passed as props.
  const [ctxAppeal, ctxFullName] = useOutletContext() || [];
  const appeal = propAppeal ?? ctxAppeal;
  const fullName = propFullName ?? ctxFullName;
  const {
    events,
    alerts,
    status,
    docket,
    incompleteHistory,
    location,
    aod,
    active: appealIsActive,
    type: appealAction,
  } = appeal.attributes;
  const currentStatus = getStatusContents(appeal, fullName);
  const nextEvents = getNextEvents(appeal);

  // Gates the What's Next and Docket chunks
  const hideDocketStatusTypes = [
    STATUS_TYPES.pendingSoc,
    STATUS_TYPES.pendingForm9,
    STATUS_TYPES.decisionInProgress,
    STATUS_TYPES.bvaDevelopment,
  ];
  const hideDocketAppealActions = [
    APPEAL_ACTIONS.reconsideration,
    APPEAL_ACTIONS.cue,
    APPEAL_ACTIONS.other,
  ];

  let shouldShowDocket;
  let isAppeal;

  switch (appeal.type) {
    case APPEAL_TYPES.legacy:
      shouldShowDocket =
        appealIsActive &&
        !hideDocketStatusTypes.includes(status.type) &&
        !hideDocketAppealActions.includes(appealAction);
      isAppeal = true;
      break;
    case APPEAL_TYPES.appeal:
      shouldShowDocket =
        appealIsActive &&
        location === 'bva' &&
        status.type !== STATUS_TYPES.decisionInProgress;
      isAppeal = true;
      break;
    default:
      shouldShowDocket = false;
      isAppeal = false;
  }

  const afterNextAlertTypes = [
    ALERT_TYPES.cavcOption,
    ALERT_TYPES.amaPostDecision,
  ];

  // These alerts repeat the status body for their own status, so they're hidden
  // only when the appeal has that status.
  const statusBodyAlertTypes = {
    [ALERT_TYPES.scheduledHearing]: STATUS_TYPES.scheduledHearing,
    [ALERT_TYPES.blockedByVso]: STATUS_TYPES.atVso,
  };

  const filteredAlerts = alerts.filter(
    a =>
      !afterNextAlertTypes.includes(a.type) &&
      statusBodyAlertTypes[a.type] !== status.type,
  );
  const afterNextAlerts = (
    <div>
      {alerts
        .filter(a => afterNextAlertTypes.includes(a.type))
        .map((a, i) => {
          const alert = getAlertContent(a, appealIsActive);
          return (
            <div key={`after-next-alert-${i}`}>
              <h2>{alert.title}</h2>
              <div>{alert.description}</div>
            </div>
          );
        })}
    </div>
  );

  return (
    <div id="tabPanelv2status">
      <CurrentStatus
        title={currentStatus.title}
        description={currentStatus.description}
        isClosed={!appealIsActive}
        isAppeal={isAppeal}
      />
      <AlertsList alerts={filteredAlerts} appealIsActive />
      {nextEvents.events.length > 0 && <WhatsNext nextEvents={nextEvents} />}
      {shouldShowDocket && (
        <Docket {...docket} aod={aod} appealAction={appealAction} />
      )}
      {afterNextAlerts}
      <PastEventsSection events={events} missingEvents={incompleteHistory} />
    </div>
  );
}

AppealsV2StatusPage.propTypes = {
  appeal: PropTypes.shape({
    type: PropTypes.string,
    attributes: PropTypes.object.isRequired,
  }),
  fullName: PropTypes.shape({
    first: PropTypes.string,
    middle: PropTypes.string,
    last: PropTypes.string,
  }),
};
